<template>
  <v-card flat>
    <v-row class="d-flex">
      <v-col cols="12" class="pb-2">
        <v-btn color="primary" icon @click="handleBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-h6 mr-2" style="vertical-align: middle"
          >{{ $t('message.ai.mindmap.mindmap') }}
        </span>
        <helper-tooltip :click="handleKnowMore" />
      </v-col>
      <v-col cols="12" class="pb-6">
        <span class="ml-2 mb-3 text-body-1 textType2--text">{{
          $t('message.ai.mindmap.mindMapDescription')
        }}</span>
      </v-col>
      <v-container :class="{ 'd-none': !isLoading }">
        <v-row class="d-flex">
          <v-col cols="12">
            <v-skeleton-loader
              class="border loader-width ml-3"
              type="chip"
            ></v-skeleton-loader>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader
              class="border loader-width ml-3 pr-5"
              type="image"
            ></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>
      <div :class="{ 'd-none': isLoading }" style="width: 100%">
        <v-row no-gutters class="pa-3 pt-1">
          <v-col cols="9" class="d-flex align-center pb-2">
            <v-switch
              v-model="isEnabledInPlayer"
              :disabled="preventToggleQuickly"
              @click="toggleActivation"
              class="my-0 py-0 ml-2 align-center"
              compact
              hide-details
            ></v-switch
            ><span class="ml-1 text-body-1 textType--text">{{
              $t('message.ai.mindmap.enableMindmap')
            }}</span>
          </v-col>
        </v-row>
      </div>
      <v-row>
        <v-col cols="auto" v-if="canShowDownloadBtn">
          <v-btn
            :class="{ 'd-none': isLoading }"
            small
            class="ml-6 elevation-0 mt-2"
            color="primary"
            @click="handleDownloadMindMap"
          >
            {{ $t('message.ai.downloadPDF') }}
          </v-btn>
        </v-col>
        <v-col
          cols="auto"
          class="pl-0"
          :class="{ 'ml-8': !canShowDownloadBtn }"
        >
          <v-btn
            class="ml-0 mt-2"
            color="primary"
            outlined
            small
            :class="{ 'd-none': isLoading }"
            @click="openEditMindMapModal"
            >{{ $t('message.ai.mindmap.edit') }}</v-btn
          >
        </v-col>
      </v-row>

      <MindmapEditModal :videoId="video.id"></MindmapEditModal>
      <div class="row-container">
        <div
          class="mt-8 ml-6 mr-2 flexible-div"
          :class="{ 'display-mindmap': !isEnabledInPlayer }"
        >
          <div class="scroll-container">
            <pre
              :key="componentKey"
              id="mindmap-container"
              class="mermaid"
            ></pre>
          </div>
        </div>
      </div>
    </v-row>
  </v-card>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import Panzoom from 'panzoom'
import playerApi from '@/services/playerApi.js'
import videosApi from '@/services/videosApi.js'
import linkHelper from '@/utils/linkHelper.mixin.js'
import HelperTooltip from '@/components/HelperTooltip.vue'
import { CONFIG_URL } from '@/config/envs.js'
import MindmapEditModal from '@/components/MindmapEditModal.vue'
import Storage from '@/utils/Storage'

export default {
  name: 'VideoPlayerMindMap',
  mixins: [linkHelper],
  components: { HelperTooltip, MindmapEditModal },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      preventToggleQuickly: false,
      mermaidScriptLoaded: false,
      mermaid: '',
      mermaidDiagram: '',
      isEnabledInPlayer: false,
      mindMap: '',
      videoConfig: {},
      isLoading: true,
      mindmapString: '',
      componentKey: 1,
    }
  },
  created() {
    this.checkEnabledItems()
  },
  mounted() {
    this.handleData()
    this.$root.$on('renderDiagram', async (myConvertedModel) => {
      this.mermaidScriptLoaded = false
      this.mindmap = myConvertedModel
      this.componentKey += 1
      await this.renderDiagram()
    })
  },
  computed: {
    ...mapState({
      hasData: (state) => state.video.aiWorkFlow?.data,
      video: (state) => state.video.video,
    }),
    canShowDownloadBtn() {
      if (this.video?.config?.ai?.pdf_generated) {
        return this.video?.config?.ai?.pdf_generated
      }
      return false
    },
  },
  methods: {
    ...mapMutations('video', ['UPDATE_AI_VIDEO_CONFIG', 'UPDATE_VIDEO_CONFIG']),
    ...mapActions('video', ['handleFetchAiWorkFlowData']),
    handleDownloadMindMap() {
      const url = `${CONFIG_URL}/${this.video.pullzone_name}/${this.video.video_external_id}/mindmap.pdf`
      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const file = window.URL.createObjectURL(blob)
          const tempDownloadTag = document.createElement('a')
          tempDownloadTag.href = file
          tempDownloadTag.download = 'mindmap.pdf'
          document.body.appendChild(tempDownloadTag)
          tempDownloadTag.click()
          tempDownloadTag.remove()
        })
        .catch((err) => {
          console.log(err)
        })
    },
    handleKnowMore() {
      const link = this.getMindMapLink()
      window.open(link, '_blank')
    },
    async loadMermaidScript() {
      return new Promise((resolve) => {
        const script = document.createElement('script')
        script.src =
          'https://cdn.jsdelivr.net/npm/mermaid@10.6.1/dist/mermaid.min.js'

        script.onload = () => {
          this.mermaidScriptLoaded = true
          this.mermaid = window.mermaid

          resolve()
        }
        document.head.appendChild(script)
      })
    },
    async renderDiagram() {
      if (!this.mermaidScriptLoaded) {
        await this.loadMermaidScript()
      }

      if (this.mermaid) {
        this.mermaidDiagram = this.jsonToMermaid(this.mindmap)
        this.addMindmapToLocalStorage(JSON.stringify(this.mindmap))

        const mermaidContainer = document.getElementById('mindmap-container')
        if (mermaidContainer) {
          mermaidContainer.textContent = this.mermaidDiagram
          Panzoom(mermaidContainer, {
            zoomSpeed: 0.065,
            maxZoom: 10,
            minZoom: 0.5,
          })
          window.mermaid.initialize({
            startOnLoad: true,
            flowchart: {
              useMaxWidth: false,
              htmlLabels: true,
              curve: 'linear',
            },
          })
          // window.mermaid.initialize()
          try {
            await window.mermaid.run({
              querySelector: '#mindmap-container',
            })
          } catch (error) {
            console.log(error)
          }
        }
        this.preventToggleQuickly = false
      }
    },
    jsonToMermaid(json) {
      let mermaidStr = `mindmap ${json.theme}\n`
      let nodeCount = 0

      function breakText(text, maxLineLength = 20) {
        return text.replace(
          new RegExp(`(.{1,${maxLineLength}})(\\s+|$)`, 'g'),
          '$1\n'
        )
      }

      function processNode(node, indent, isRoot = false) {
        let nodeId = `N${nodeCount++}`
        let nodeLabel = node.text ? node.text : `Node ${nodeId}`
        nodeLabel = breakText(nodeLabel)

        if (!isRoot) {
          mermaidStr += `${indent}${nodeId}("${nodeLabel}")\n`
        }

        if (node.children) {
          let childIndent = indent + '    '
          node.children.forEach((child) => {
            mermaidStr += `${indent}    ${nodeId} --> `
            processNode(child, childIndent)
          })
        }
      }

      processNode(json, '', true)
      return mermaidStr
    },
    async handleData() {
      if (this.hasData) {
        const { mindmap } = this.hasData

        this.mindmap = mindmap
        await this.renderDiagram()
      } else {
        let { pullzone_name, video_external_id } = this.video
        try {
          const { mindmap } = await this.handleFetchAiWorkFlowData({
            pullzone_name,
            video_external_id,
          })

          this.mindmap = mindmap
          await this.renderDiagram()
        } catch (error) {
          console.error(error)
        }
      }
    },

    async toggleActivation() {
      this.preventToggleQuickly = true
      this.videoConfig['mindmap'] = this.isEnabledInPlayer
      await playerApi.updateAiVideoConfig(this.video.id, this.videoConfig)
      this.preventToggleQuickly = false
    },
    async checkEnabledItems() {
      const {
        data: { config },
      } = await videosApi.getAVideo(this.video.id)
      this.UPDATE_VIDEO_CONFIG(config)
      this.isEnabledInPlayer = config.ai.mindmap
      this.videoConfig = config.ai
      this.isLoading = false
    },
    openEditMindMapModal() {
      this.$root.$emit('openEditMindMapModal')
    },
    addMindmapToLocalStorage(val) {
      const key = 'mindmapString'
      Storage.setItem(key, val)
    },
  },
}
</script>

<style lang="scss" scoped>
#mermaid-container {
  display: flex;
  justify-self: center;
  align-items: baseline;
  width: auto;
  height: auto;
  // width: 100%;
  // height: 100%;
}

.scroll-container {
  flex-grow: 1;
  overflow: hidden;
  white-space: nowrap;
  cursor: grab;
  // width: 450px;
}

.row-container {
  display: flex;
  width: 100%;
  margin-right: 15px;
}
.flexible-div {
  flex-grow: 1;
}

.loader-width {
  width: 100%;
}

.scroll-container:active {
  cursor: grabbing;
}

.display-mindmap {
  visibility: hidden;
}
</style>
